/**
 * The note lane: a line of its own along the bottom of a row, where every cell's note goes.
 *
 * A block's top line is for its times and the line below for its name; neither is shared with the
 * note, so blocks look alike whatever their width. A row holding a noted cell is made one line
 * taller, and each note sits in that line, under its cell, reaching sideways over the empty lane
 * beside it for as much room as its text needs — on a second line when two notes would collide.
 * The label and times keep the whole height above, so nothing is displaced.
 *
 * Pure geometry: text widths come from a callback, so this is testable without a canvas.
 */
import { ellipsize } from './segmentLayout';

/** Whether a cell's note goes in the lane — every note does. */
export function needsNoteLane(note: string | undefined): boolean {
  return !!note;
}

/** Height of the lane for an annotation font size: the pill (font + 1) and a pixel above and below. */
export function noteLaneHeight(annSize: number): number {
  return Math.ceil(annSize) + 3;
}

export interface LaneItem {
  /** Left edge of the noted cell. */
  x: number;
  /** Width of the noted cell: the pill is centred on it. Absent = left-aligned at `x`. */
  w?: number;
  note: string;
}

export interface LanePill {
  /** Text to draw — possibly cut with an ellipsis, '' for a bare marker pill. */
  text: string;
  x: number;
  width: number;
  /** Which line of the lane it sits on, 0 = top. */
  track: number;
}

/** The most lines a lane may use: two narrow notes side by side get a line each rather than one cut short. */
export const NOTE_LANE_MAX_TRACKS = 3;

/**
 * Where each lane note's pill goes, and how many lines the lane needs.
 *
 * A pill starts at its cell's left edge and is as wide as its whole text wants. When the next
 * cell's note would start under the end of an earlier pill, it goes on a second line instead of
 * cutting either short (up to NOTE_LANE_MAX_TRACKS). A pill is cut with an ellipsis only where
 * the text runs into the end of the row; too little room for any text leaves a bare marker, and a
 * note with no free line at its position gets null, which the caller marks with the corner flag.
 *
 * `items` must be in left-to-right order.
 */
export function layoutLaneTracks(
  items: LaneItem[],
  rowRight: number,
  measure: (s: string) => number,
  opts: { padX?: number; gap?: number; minBarePx?: number; maxTracks?: number; rowLeft?: number } = {},
): { pills: Array<LanePill | null>; tracks: number } {
  const padX = opts.padX ?? 2;
  const gap = opts.gap ?? 1;
  const minBare = opts.minBarePx ?? 6;
  const maxTracks = opts.maxTracks ?? NOTE_LANE_MAX_TRACKS;
  const trackEnd: number[] = new Array(maxTracks).fill(-Infinity);
  let tracks = 0;

  const rowLeft = opts.rowLeft ?? 0;

  const pills = items.map((item): LanePill | null => {
    // Centred on its cell, as wide as its text wants but never past the row's ends; a pill with no
    // cell width starts at the cell's left edge instead.
    const centre = item.w === undefined ? undefined : item.x + item.w / 2;
    const avail = centre === undefined ? rowRight - item.x : 2 * Math.min(centre - rowLeft, rowRight - centre);
    if (!(avail >= minBare)) return null;
    const want = measure(item.note) + padX * 2;
    const text = want <= avail ? item.note : ellipsize(item.note, avail - padX * 2, measure);
    const width = text ? Math.min(measure(text) + padX * 2, avail) : Math.min(minBare, avail);
    const left = centre === undefined ? item.x : centre - width / 2;

    // First line free at this pill's left edge: a later note drops to a second line only when an
    // earlier pill really is in its way.
    let track = -1;
    for (let t = 0; t < maxTracks; t++) if (left >= trackEnd[t] + gap) { track = t; break; }
    if (track < 0) return null;
    trackEnd[track] = left + width;
    tracks = Math.max(tracks, track + 1);
    return { text, x: left, width, track };
  });
  return { pills, tracks };
}
